import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick } from "vue";
import { storeToRefs } from "pinia";
import router from "@/router";
import { useEUAccountsStore } from '@/store_v3/EUAccountsStore';
import { type ITableHeader, showServerError } from "@/utils";
import { updateQueryString } from "@/utils/updateQueryString";
import { CellsType, prepareItems, type RawConfig } from "@/utils/CellsFactory";
import { IEUAccountServices, IOfferService } from "@/api/types/revenue";
import { getOfferServices } from "@/api/revenue";
import { getCurrentDate } from "@/utils/formatDates";
import { translateGoalStatus } from "@/utils/translate";
import { getCurrencyIcon } from "@/utils/translate";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import BaseInput from '@/components-UI/BaseInput.vue';
import BaseCheckbox from "@/components-UI/BaseCheckbox.vue";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import SvgSearch from "@/assets/icons/search.svg";
import SvgEyeOn from "@/assets/icons/eye.svg";
import SvgEyeOff from "@/assets/icons/eye-off.svg";
import SvgPlus from "@/assets/icons/plus.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import { getFormatDate } from "@/utils/formatDates";
import BaseDialog from "@/components-UI/modals/BaseDialog.vue";
import SvgCheck from "@/assets/icons/check.svg";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import { isEuAccountFixedGoal, isEuAccountPercentGoal } from "@/views/revenue/EUAccounts/euAccountGoalRevenue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentServicesTabMain',
  setup(__props) {

const EUAccountStore = useEUAccountsStore();
const {
    isLoading,
    form,
    getServicesTotalSum,
    isEdit,
} = storeToRefs(EUAccountStore);
const {
    formatNumber,
    fetchRate,
    recountServicesSum,
    recountServiceRateFromTotal,
} = EUAccountStore;

const isPercentGoalService = (item: IEUAccountServices): boolean => isEuAccountPercentGoal(item);

const isFixedGoalService = (item: IEUAccountServices): boolean => isEuAccountFixedGoal(item);

/** Курс нужен только если валюта услуги отличается от валюты счёта и цель не процентная (как в колонке «Курс») */
const isExchangeRateRequired = (item: IEUAccountServices): boolean => {
    if (isPercentGoalService(item)) return false;
    return (item?.goal_currency !== form.value.currency && item?.currency !== form.value.currency) === true;
};

/** Ставка оффера за процент фиксирована: берём её из услуги или из списка услуг за период */
const getGoalPercentRate = (item: IEUAccountServices): string => {
    const percent = item.goal_revenue_percent
        ?? tableItemsTop.value.find((el) => el.offer_id === item.offer_id && (
            el.goal_service_id === item.goal_service_id
            || el.goal_id === item.goal_id
            || el.goal_id === item.goal_service_id
        ))?.goal_revenue_percent;

    if (percent == null) return '—';

    return `${formatNumber(percent, [2, 0]).replace(/,0+$/, '').replace(/,$/, '')} %`;
};

/** «Итого» вводит менеджер — под него подстраивается ставка (количество не трогаем) */
const onServiceTotalUpdate = (item: IEUAccountServices, value: number | null): void => {
    item.final_amount = value ?? 0;
    recountServiceRateFromTotal(item);
};

/** При смене количества пересчитываем итого, а не ставку */
const onServiceQuantityUpdate = (item: IEUAccountServices, value: number | null): void => {
    item.quantity = value ?? 1;
    recountServicesSum(item);
};

const searchValue = ref('');
const inactiveTargetsValue = ref(false);
const offersWithoutConversionsValue = ref(false);
const isConfirmDelete = ref(false);

const tableItemsTop = ref<IOfferService[]>([]);
const tableOffset = ref(0);
const tableHeadersTop: ITableHeader[] = [
    { text: 'ID оффера', value: 'offer_id', width: '77px', align: 'center' },
    { text: 'Оффер', value: 'offer_name', width: 'auto' },
    { text: 'ID цели', value: 'goal_service_id', width: '77px', align: 'center' },
    { text: 'Цель', value: 'goal_name', width: 'auto' },
    { text: 'Ставка', value: 'goal_revenue', width: '112px', align: 'end' },
    { text: 'Валюта', value: 'goal_currency', width: '64px', align: 'end' },
    { text: 'Конверсии', value: 'conversions_total', width: '112px', align: 'end' },
    { text: 'Принято конв.', value: 'conversions_approved', width: '112px', align: 'end' },
    { text: 'Статус', value: 'goal_status', width: '102px', align: 'end' },
    { text: '', value: 'actions', width: '48px', align: 'center' },
];
const tableConfigsTop: RawConfig<IOfferService & { goal_revenue: number, actions: any }> = {
    [CellsType.Route]: {
        offer_id: {
            name: 'offer',
            isChip: true,
            valueKey: 'offer_id',
        },
        offer_name: {
            name: 'offer',
            valueKey: 'offer_id',
            text: 'offer_name',
        },
        goal_service_id: {
            name: 'offer-goal',
            isChip: true,
            valueKey: 'goal_service_id',
            text: 'goal_service_id',
            query: (i: IOfferService) => ({ offer_id: i.offer_id }),
        },
        goal_name: {
            name: 'offer-goal',
            valueKey: 'goal_service_id',
            text: 'goal_name',
            query: (i: IOfferService) => ({ offer_id: i.offer_id }),
        },
    },
    [CellsType.Number]: {
        goal_revenue: {
            value: (i: IOfferService) => {
                if (isEuAccountFixedGoal(i)) {
                    return formatNumber(i.goal_revenue_flat!);
                }
                if (isEuAccountPercentGoal(i)) {
                    return `${formatNumber(i.goal_revenue_percent!, [2, 0]).replace(/,0+$/, '').replace(/,$/, '')} %`;
                }

                return 0;
            },
        },
        conversions_total: {
            digits: 0,
        },
        conversions_approved: {
            digits: 0,
        },
    },
    [CellsType.Status]: {
        goal_status: {
            translate: (status) => translateGoalStatus(status),
        },
    },
    [CellsType.Uniq]: {
        actions: {},
        goal_currency: {},
    },
};
const filteredTableItemsTop = computed<IOfferService[]>(() => {
    let filtered = tableItemsTop.value;

    if (searchValue.value.trim()) {
        const searchLower = searchValue.value.toLowerCase().trim();

        filtered = filtered.filter((item) => {
            const offerId = String(item.offer_id || '').toLowerCase();
            const goalId = String(item.goal_id || '').toLowerCase();
            const offerName = String(item.offer_name || '').toLowerCase();
            const goalName = String(item.goal_name || '').toLowerCase();

            return (
                offerId.includes(searchLower) ||
                goalId.includes(searchLower) ||
                offerName.includes(searchLower) ||
                goalName.includes(searchLower)
            );
        });
    }

    if (!inactiveTargetsValue.value) {
        filtered = filtered.filter((item) => item.goal_status === true);
    }

    if (!offersWithoutConversionsValue.value) {
        filtered = filtered.filter((item) => (item.conversions_total || 0) > 0);
    }

    return filtered;
});
const getTableTopFormatItems = computed(() =>
    prepareItems(
        filteredTableItemsTop.value,
        tableConfigsTop,
        tableHeadersTop.map(h => h.value),
    ),
);

const tableHeadersBottom: ITableHeader[] = [
    { text: 'ID оффера', value: 'offer_id', width: '77px', align: 'center' },
    { text: 'Оффер', value: 'offer_name', width: 'auto' },
    { text: 'ID цели', value: 'goal_service_id', width: '77px', align: 'center' },
    { text: 'Цель', value: 'goal_name', width: 'auto' },
    { text: 'Ставка', value: 'original_amount', width: '176px', align: 'right' },
    { text: 'Курс', value: 'exchange_rate', width: '100px', align: 'right' },
    { text: 'Количество', value: 'quantity', width: '104px', align: 'center' },
    { text: 'Итого', value: 'final_amount', width: '112px', align: 'right' },
    { text: '', value: 'actions', width: '48px', align: 'center' },
];
const tableConfigsBottom: RawConfig<IEUAccountServices & { actions: any }> = {
    [CellsType.Route]: {
        offer_id: {
            name: 'offer',
            isChip: true,
            valueKey: 'offer_id',
        },
        offer_name: {
            name: 'offer',
            valueKey: 'offer_id',
            text: 'offer_name',
        },
        // Пока отправлять на деталку оффера
        goal_service_id: {
            name: 'offer', // offer-goal
            isChip: true,
            valueKey: 'offer_id', // goal_service_id
            text: 'goal_service_id',
            // query: (i: IOfferService) => ({ offer_id: i.offer_id }),
        },
        goal_name: {
            name: 'offer', // offer-goal
            valueKey: 'offer_id', // goal_service_id
            text: 'goal_name',
            // query: (i: IOfferService) => ({ offer_id: i.offer_id }),
        },
    },
    [CellsType.Uniq]: {
        actions: {},
        original_amount: {},
        exchange_rate: {},
        quantity: {},
        final_amount: {},
    },
};
const getTableBottomFormatItems = computed(() =>
    prepareItems(
        form.value.services,
        tableConfigsBottom,
        tableHeadersBottom.map(h => h.value),
    ),
);

const isSingle = computed(() => router.currentRoute.params.id !== 'create');

const addItemInTableBottom = async (item: IEUAccountServices): Promise<void> => {
    const customItem: IEUAccountServices = JSON.parse(JSON.stringify({
        ...item,
        tempId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        quantity: 1,
        original_amount: 0,
        final_amount: 0,
        exchange_rate: 0,
        operation: '',
        original_service_name: item.service_name,
    }));

    const isPercentGoal = isEuAccountPercentGoal(customItem);
    if (!isPercentGoal && form.value.currency !== customItem.goal_currency) {
        const { operation, original_rate } = await fetchRate({
            from: item.goal_currency,
            to: form.value.currency,
            date: getCurrentDate('yyyy-MM-dd'),
        });

        customItem.exchange_rate = original_rate as number;
        customItem.operation = operation as string;
    }

    if (isEuAccountFixedGoal(customItem) && customItem.goal_revenue_flat) {
        customItem.original_amount = customItem.goal_revenue_flat;
    }

    form.value.services.push(customItem);
    recountServicesSum(customItem);
};

const clearBottomTable = (): void => {
    form.value.services = [];
    isConfirmDelete.value = false;
};

const removeItemFromTableBottom = (item: IEUAccountServices): void => {
    const index = form.value.services.findIndex((el: IOfferService) => el.tempId === item.tempId);
    if (index !== -1) {
        form.value.services.splice(index, 1);
    }
};

const fetchOfferServicesItems = async (): Promise<void> => {
    if (!form.value.advertiser_id || !form.value.period) return;

    try {
        isLoading.services = true;

        tableItemsTop.value = await getOfferServices(
            form.value.advertiser_id,
            { period: getFormatDate(form.value.period, 'yyyy-MM') },
        );
    } catch (e) {
        showServerError(e, 'Список услуг не загружен');
    } finally {
        isLoading.services = false;
    }
};

watch([searchValue, inactiveTargetsValue, offersWithoutConversionsValue], () => {
    tableOffset.value = 0;
    const query = router.currentRoute.query;
    const value = query.offset;
    const rawValue = Array.isArray(value) ? value[0] : value;
    const parsed = Number(rawValue);

    const currentOffset = Number.isFinite(parsed) ? parsed : 0;

    if (currentOffset <= 0) return;

    updateQueryString({
        ...router.currentRoute.query,
        offset: 0,
    });
});

watch(() => form.value.currency, async (val: string) => {
    if (!val || !form.value.services?.length) return;
    if (isSingle.value && !isEdit.value) return;
    await Promise.all(form.value.services.map(async (el: IEUAccountServices) => {
        const isPercentGoal = isEuAccountPercentGoal(el);
        if (!isPercentGoal && form.value.currency !== el.goal_currency) {
            const { operation, original_rate } = await fetchRate({
                from: el.goal_currency,
                to: form.value.currency,
                date: getCurrentDate('yyyy-MM-dd'),
            });

            el.exchange_rate = original_rate;
            el.operation = operation;
        }
    }));

    form.value.services.forEach((el: IEUAccountServices) => recountServicesSum(el));
});

watch([() => form.value.advertiser_id, () => form.value.period], () => {
    if (!isSingle.value && isEdit.value) {
        form.value.services = [];
        tableItemsTop.value = [];

        if (form.value.advertiser_id && form.value.period) {
            nextTick(() => fetchOfferServicesItems());
        }
    }
});

watch([() => form.value.period, isEdit], () => {
    if (isSingle.value && form.value.advertiser_id && form.value.period) {
        tableItemsTop.value = [];
        nextTick(() => fetchOfferServicesItems());
    }
}, { immediate: true });

return { __sfc: true,EUAccountStore, isLoading, form, getServicesTotalSum, isEdit, formatNumber, fetchRate, recountServicesSum, recountServiceRateFromTotal, isPercentGoalService, isFixedGoalService, isExchangeRateRequired, getGoalPercentRate, onServiceTotalUpdate, onServiceQuantityUpdate, searchValue, inactiveTargetsValue, offersWithoutConversionsValue, isConfirmDelete, tableItemsTop, tableOffset, tableHeadersTop, tableConfigsTop, filteredTableItemsTop, getTableTopFormatItems, tableHeadersBottom, tableConfigsBottom, getTableBottomFormatItems, isSingle, addItemInTableBottom, clearBottomTable, removeItemFromTableBottom, fetchOfferServicesItems, getCurrencyIcon, BaseTable, BaseButton, BaseInput, BaseCheckbox, BaseInputNumber, SvgSearch, SvgEyeOn, SvgEyeOff, SvgPlus, SvgTrash, BaseEditableCard, BaseDialog, SvgCheck, BaseQuestionMarkTooltip, isEuAccountPercentGoal }
}

})